import moment from "moment";
import { useCallback } from "react";
import { GetFormattedDate } from "../../helpers";
import CommonValidatorProps from "./commonProps";
import useValidator from "./useValidator";
import { useTranslation } from "react-i18next";

export interface DateValidatorProps extends CommonValidatorProps<Date> {
  required?: boolean;
  before?: Date;
  after?: Date;
  notBefore?: Date;
  notAfter?: Date;
  onlyInFuture?: boolean;
}

const useDateValidator = (props: DateValidatorProps) => {
  const { t } = useTranslation();
  const { required, before, after, notBefore, notAfter, onlyInFuture } = props;

  const validateRequired = useCallback(
    (value?: Date): [boolean, string] => {
      return [required ? value !== undefined && value !== null : true, t("field:helper.fieldIsRequired")];
    },
    [required]
  );

  const validateBefore = useCallback(
    (value?: Date): [boolean, string] => {
      return [
        before ? moment(value?.toString()).isBefore(before?.toString()) : true,
        `Date must be before ${GetFormattedDate(before)}`,
      ];
    },
    [before]
  );

  const validateAfter = useCallback(
    (value?: Date): [boolean, string] => {
      return [
        after ? moment(value?.toString()).isAfter(after?.toString()) : true,
        `Date must be after ${GetFormattedDate(after)}`,
      ];
    },
    [after]
  );

  const validateNotBefore = useCallback(
    (value?: Date): [boolean, string] => {
      return [
        notBefore ? !moment(value?.toString()).isBefore(notBefore?.toString()) : true,
        `Date must not be before ${GetFormattedDate(notBefore)}`,
      ];
    },
    [notBefore]
  );

  const validateNotAfter = useCallback(
    (value?: Date): [boolean, string] => {
      return [
        notAfter ? !moment(value?.toString()).isAfter(notAfter?.toString()) : true,
        `Date must not be after ${GetFormattedDate(notAfter)}`,
      ];
    },
    [notAfter]
  );

  const validateOnlyInFuture = useCallback(
    (value?: Date): [boolean, string] => {
      if (!value) return [true, "Bypassed"];
      return [onlyInFuture ? moment(value?.toString()).isSameOrAfter(moment()) : true, "Date must be in future"];
    },
    [onlyInFuture]
  );

  const validator = useValidator({
    ...props,
    validators: [
      validateRequired,
      validateBefore,
      validateAfter,
      validateNotBefore,
      validateNotAfter,
      validateOnlyInFuture,
    ],
  });

  return validator;
};

export default useDateValidator;
